import { create } from 'zustand';
import { AxiosError } from 'axios';
import {
  UtilityType,
  UtilitySettingsSchema,
  UtilityMonthSchema,
  UtilityProofFileSchema,
  PatchUtilitySettingsRequest,
} from '../types/utility-proof.type';
import {
  getUtilitySettings,
  patchUtilitySettings as patchUtilitySettingsService,
  initOrGetUtilityMonth,
  listUtilityProofFiles,
  addUtilityProofFiles,
  generateUtilityProofPresignedUrl,
  parseUtilityProofPresignedResponse,
  uploadFileToPresignedUrl,
  getAllowedExtension,
} from '../services/utility-proof.service';

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

function extractError(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    return (
      (error.response?.data as { detail?: string })?.detail || error.message
    );
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

/** API may omit or null `required_utilities`; spreading it in the UI throws. */
function normalizeUtilitySettings(raw: UtilitySettingsSchema): UtilitySettingsSchema {
  return {
    collection_site_id: raw.collection_site_id,
    utilities_not_required: Boolean(raw.utilities_not_required),
    required_utilities: Array.isArray(raw.required_utilities)
      ? raw.required_utilities
      : [],
    lock_after_days:
      typeof raw.lock_after_days === 'number' && Number.isFinite(raw.lock_after_days)
        ? raw.lock_after_days
        : 7,
    updated_at: raw.updated_at ?? '',
  };
}

function normalizeProofFile(raw: UtilityProofFileSchema): UtilityProofFileSchema {
  return {
    ...raw,
    download_url: raw.download_url ?? null,
  };
}

const makeUtilityMap = <T>(): Record<UtilityType, T> => ({});

interface UtilityProofState {
  settings: UtilitySettingsSchema | null;
  utilityMonth: UtilityMonthSchema | null;
  proofFiles: UtilityProofFileSchema[];

  isLoadingSettings: boolean;
  isLoadingMonthAndProofs: boolean;
  isSavingSettings: boolean;
  uploadingUtilities: Record<UtilityType, boolean>;

  settingsError: string | null;
  monthError: string | null;
  uploadErrors: Record<UtilityType, string | null>;

  clearSettingsError: () => void;
  clearMonthError: () => void;
  clearUploadError: (utilityType: UtilityType) => void;
  resetMonthAndProofs: () => void;
  reset: () => void;

  fetchSettings: (siteId: string) => Promise<void>;
  saveSettings: (siteId: string, body: PatchUtilitySettingsRequest) => Promise<boolean>;
  fetchMonthAndProofs: (siteId: string, year: number, month: number) => Promise<void>;
  refreshProofs: (siteId: string, year: number, month: number) => Promise<void>;
  uploadProofsForUtility: (
    siteId: string,
    year: number,
    month: number,
    utilityType: UtilityType,
    files: File[]
  ) => Promise<boolean>;
}

export const useUtilityProofStore = create<UtilityProofState>((set, get) => ({
  settings: null,
  utilityMonth: null,
  proofFiles: [],

  isLoadingSettings: false,
  isLoadingMonthAndProofs: false,
  isSavingSettings: false,
  uploadingUtilities: makeUtilityMap<boolean>(),

  settingsError: null,
  monthError: null,
  uploadErrors: makeUtilityMap<string | null>(),

  clearSettingsError: () => set({ settingsError: null }),
  clearMonthError: () => set({ monthError: null }),
  clearUploadError: (utilityType) =>
    set((state) => ({
      uploadErrors: { ...state.uploadErrors, [utilityType]: null },
    })),

  resetMonthAndProofs: () =>
    set({
      utilityMonth: null,
      proofFiles: [],
      isLoadingMonthAndProofs: false,
      uploadingUtilities: makeUtilityMap<boolean>(),
      monthError: null,
      uploadErrors: makeUtilityMap<string | null>(),
    }),

  reset: () =>
    set({
      settings: null,
      utilityMonth: null,
      proofFiles: [],
      isLoadingSettings: false,
      isLoadingMonthAndProofs: false,
      isSavingSettings: false,
      uploadingUtilities: makeUtilityMap<boolean>(),
      settingsError: null,
      monthError: null,
      uploadErrors: makeUtilityMap<string | null>(),
    }),

  fetchSettings: async (siteId) => {
    set({ isLoadingSettings: true, settingsError: null });
    try {
      const response = await getUtilitySettings(siteId);
      const data = response as GenericResponse<UtilitySettingsSchema>;
      set({
        settings: normalizeUtilitySettings(data.result),
        isLoadingSettings: false,
      });
    } catch (error) {
      set({
        settings: null,
        isLoadingSettings: false,
        settingsError: extractError(error, 'Failed to load utility settings'),
      });
    }
  },

  saveSettings: async (siteId, body) => {
    set({ isSavingSettings: true, settingsError: null });
    try {
      const response = await patchUtilitySettingsService(siteId, body);
      const data = response as GenericResponse<UtilitySettingsSchema>;
      set({
        settings: normalizeUtilitySettings(data.result),
        isSavingSettings: false,
      });
      return true;
    } catch (error) {
      set({
        isSavingSettings: false,
        settingsError: extractError(error, 'Failed to save utility settings'),
      });
      return false;
    }
  },

  fetchMonthAndProofs: async (siteId, year, month) => {
    set({ isLoadingMonthAndProofs: true, monthError: null });
    try {
      const monthResponse = await initOrGetUtilityMonth(siteId, year, month);
      const monthData = monthResponse as GenericResponse<UtilityMonthSchema>;
      const monthResult = monthData.result;
      const normalizedMonth: UtilityMonthSchema | null = monthResult
        ? {
            ...monthResult,
            month_key:
              monthResult.month_key ||
              `${year}-${String(month).padStart(2, '0')}`,
            required_utilities_snapshot: Array.isArray(
              monthResult.required_utilities_snapshot
            )
              ? monthResult.required_utilities_snapshot
              : [],
          }
        : null;

      const proofsResponse = await listUtilityProofFiles(siteId, year, month);
      const proofsData = proofsResponse as GenericResponse<UtilityProofFileSchema[]>;
      const rawProofs = proofsData.result ?? [];

      set({
        utilityMonth: normalizedMonth,
        proofFiles: rawProofs.map(normalizeProofFile),
        isLoadingMonthAndProofs: false,
      });
    } catch (error) {
      set({
        isLoadingMonthAndProofs: false,
        monthError: extractError(error, 'Failed to load proof documents'),
      });
    }
  },

  refreshProofs: async (siteId, year, month) => {
    try {
      const response = await listUtilityProofFiles(siteId, year, month);
      const data = response as GenericResponse<UtilityProofFileSchema[]>;
      const list = data.result ?? [];
      set({ proofFiles: list.map(normalizeProofFile) });
    } catch (error) {
      set({ monthError: extractError(error, 'Failed to refresh proof documents') });
    }
  },

  uploadProofsForUtility: async (siteId, year, month, utilityType, files) => {
    if (files.length === 0) return false;

    set((state) => ({
      uploadingUtilities: { ...state.uploadingUtilities, [utilityType]: true },
      uploadErrors: { ...state.uploadErrors, [utilityType]: null },
    }));

    try {
      const fileExtPairs = files.map((file) => {
        const ext = getAllowedExtension(file.name);
        if (!ext) {
          throw new Error(
            `Unsupported file: "${file.name}". Allowed types: pdf, jpg, jpeg, png, webp.`
          );
        }
        return { file, ext };
      });

      // Get all presigned URLs in parallel
      const presignedResults = await Promise.all(
        fileExtPairs.map(async ({ file, ext }) => {
          const res = await generateUtilityProofPresignedUrl(file.name, ext);
          const presigned = parseUtilityProofPresignedResponse(res);
          return {
            file,
            assetId: presigned.id,
            uploadUrl: presigned.upload_url,
          };
        })
      );

      // Upload bytes to presigned URLs in parallel
      await Promise.all(
        presignedResults.map(({ file, uploadUrl }) =>
          uploadFileToPresignedUrl(file, uploadUrl)
        )
      );

      // Register all asset IDs with the utility month in one call
      await addUtilityProofFiles(siteId, year, month, {
        utility_type: utilityType,
        asset_ids: presignedResults.map((r) => r.assetId),
      });

      await get().refreshProofs(siteId, year, month);

      set((state) => ({
        uploadingUtilities: { ...state.uploadingUtilities, [utilityType]: false },
      }));

      return true;
    } catch (error) {
      const message = extractError(error, 'Upload failed');
      set((state) => ({
        uploadingUtilities: { ...state.uploadingUtilities, [utilityType]: false },
        uploadErrors: { ...state.uploadErrors, [utilityType]: message },
      }));
      return false;
    }
  },
}));
